// Field library catalog — the builder sidebar's draggable/clickable field types
// in design order. Type ids are the backend elementType names (camelCase);
// their default properties live in formElementDefaults.ts.

import type { LibraryElementType } from './formElementDefaults';

export type FieldCategory = 'basic' | 'capture' | 'advanced' | 'layout';

/** dataTransfer type carrying a library item's element type during a
 *  library → canvas drag (custom MIME so foreign drags never match). */
export const FIELD_DRAG_MIME = 'application/x-midani-field';

/** Extra dataTransfer flavor set ONLY when the dragged item is a `section`.
 *  dragover may read `types` but never the data, so acceptance rules that
 *  differ for sections (sections can't nest; fields after the first section
 *  must live inside one) key off this flavor's presence. Set on BOTH drag
 *  kinds — a library add of a new section and a reorder of an existing one. */
export const SECTION_DRAG_MIME = 'application/x-midani-section';

/** dataTransfer type carrying an EXISTING canvas row's client id during a
 *  reorder drag (card grip → drop gap). Set INSTEAD of FIELD_DRAG_MIME so a
 *  gap can tell a move from a library add via `types` alone — the payload
 *  contracts differ (a client uuid vs a LibraryElementType). */
export const MOVE_DRAG_MIME = 'application/x-midani-move';

export interface FieldLibraryEntry {
  type: LibraryElementType;
  category: FieldCategory;
  /** Lucide icon name (verified against the installed lucide-react). */
  icon: string;
  /** i18n key of the display label. */
  labelKey: string;
  /** i18n key of the one-line type description (properties panel). */
  descKey: string;
}

/** Category order for the sidebar sections. */
export const FIELD_CATEGORIES: FieldCategory[] = ['basic', 'capture', 'advanced', 'layout'];

export const FIELD_LIBRARY: FieldLibraryEntry[] = [
  {
    type: 'text',
    category: 'basic',
    icon: 'type',
    labelKey: 'forms_field_text',
    descKey: 'forms_field_desc_text',
  },
  {
    type: 'number',
    category: 'basic',
    icon: 'hash',
    labelKey: 'forms_field_number',
    descKey: 'forms_field_desc_number',
  },
  {
    type: 'checkList',
    category: 'basic',
    icon: 'list-checks',
    labelKey: 'forms_field_choice_list',
    descKey: 'forms_field_desc_choice_list',
  },
  {
    type: 'dateTime',
    category: 'basic',
    icon: 'calendar-clock',
    labelKey: 'forms_field_date_time',
    descKey: 'forms_field_desc_date_time',
  },
  {
    type: 'ratingSlider',
    category: 'basic',
    icon: 'star',
    labelKey: 'forms_field_star_rating',
    descKey: 'forms_field_desc_star_rating',
  },
  {
    type: 'numberSlider',
    category: 'basic',
    icon: 'sliders-horizontal',
    labelKey: 'forms_field_number_slider',
    descKey: 'forms_field_desc_number_slider',
  },
  {
    type: 'image',
    category: 'capture',
    icon: 'image',
    labelKey: 'forms_field_image',
    descKey: 'forms_field_desc_image',
  },
  {
    type: 'signature',
    category: 'capture',
    icon: 'signature',
    labelKey: 'forms_field_signature',
    descKey: 'forms_field_desc_signature',
  },
  {
    type: 'location',
    category: 'capture',
    icon: 'map-pin',
    labelKey: 'forms_field_location',
    descKey: 'forms_field_desc_location',
  },
  {
    type: 'qrCode',
    category: 'capture',
    icon: 'qr-code',
    labelKey: 'forms_field_qr_code',
    descKey: 'forms_field_desc_qr_code',
  },
  {
    type: 'otp',
    category: 'advanced',
    icon: 'shield-check',
    labelKey: 'forms_field_otp',
    descKey: 'forms_field_desc_otp',
  },
  {
    type: 'dataSource',
    category: 'advanced',
    icon: 'database',
    labelKey: 'forms_field_data_source',
    descKey: 'forms_field_desc_data_source',
  },
  {
    type: 'sendTo',
    category: 'advanced',
    icon: 'send',
    labelKey: 'forms_field_send_to',
    descKey: 'forms_field_desc_send_to',
  },
  {
    type: 'companyStructure',
    category: 'advanced',
    icon: 'building-2',
    labelKey: 'forms_field_company_structure',
    descKey: 'forms_field_desc_company_structure',
  },
  {
    type: 'companyField',
    category: 'advanced',
    icon: 'building',
    labelKey: 'forms_field_company_field',
    descKey: 'forms_field_desc_company_field',
  },
  {
    type: 'section',
    category: 'layout',
    icon: 'rows-3',
    labelKey: 'forms_field_section',
    descKey: 'forms_field_desc_section',
  },
];
